import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useDeleteUserAccountMutation,
  useGetUserByIdQuery,
  useUpdateUserDetailsMutation,
} from "../../../features/api/user/userApi";
import { PageHeading } from "../../common/pageHeading/PageHeading";
import { Loader } from "../../common/loader/Loader";
import AlertDismissible from "../../common/alertDismissible/AlertDismissible";
import { Button, Card, Col, Container, Row } from "react-bootstrap";
import styles from "./userDetails.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faList } from "@fortawesome/free-solid-svg-icons";
import { SuccessDelete } from "../../common/successDelete/SuccessDelete";
import { useIsAdmin } from "../../../customHooks/roleChecker/useIsAdmin";
import { useIsSuperAdmin } from "../../../customHooks/roleChecker/useIsSuperAdmin";

export const UserDetails = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const isAdmin = useIsAdmin();
  const isSuperAdmin = useIsSuperAdmin();
  const [showAlert, setShowAlert] = useState(false);
  const [showStatusAlert, setShowStatusChangeAlert] = useState(false);
  const { data: userDetail, isLoading, isError } = useGetUserByIdQuery(userId);
  const [
    updateUser,
    {
      isLoading: isUpdating,
      isSuccess: isSuccessOnUpdate,
      isError: isErrOnUpdate,
    },
  ] = useUpdateUserDetailsMutation();
  const [
    deleteUser,
    {
      isLoading: isDeleting,
      isSuccess: isSuccessOnDelete,
      isError: isErrOnDelete,
    },
  ] = useDeleteUserAccountMutation();
  const handleDelete = () => {
    setShowAlert(true);
  };
  const handleConfirmOnDelete = async (confirmed) => {
    setShowAlert(confirmed);
    if (confirmed) {
      await deleteUser(userDetail.email).unwrap();
      setTimeout(() => {
        navigate("/users");
      }, 3000);
    }
    return;
  };

  const handleStatus = () => {
    setShowStatusChangeAlert(true);
  };
  const handleConfirmOnStatusChange = async (confirmed) => {
    setShowStatusChangeAlert(confirmed);
    if (confirmed && userDetail) {
      let user = { ...userDetail, isLocked: !userDetail.isLocked };
      await updateUser(user).unwrap();
      setShowStatusChangeAlert(false);
    }
    return;
  };

  // decide what to render -
  let content = null;
  if (isLoading) {
    content = (
      <div className="mb-5">
        <PageHeading title={"User details"} />
        <Loader />
      </div>
    );
  }
  if (isSuccessOnDelete) {
    content = (
      <div className="mb-5">
        <PageHeading title={"User details"} />
        <SuccessDelete />
      </div>
    );
  }
  if (isErrOnDelete || isError) {
    content = (
      <div className="mb-5">
        <PageHeading title={"User details"} />
        <p>Server error</p>
      </div>
    );
  }

  if (userDetail && !isSuccessOnDelete && !isDeleting) {
    content = (
      <div className="mb-5">
        <AlertDismissible
          title="Warning!"
          msg="Are you sure you want to delete this user account?"
          type="warning"
          showAlert={showAlert}
          onConfirm={handleConfirmOnDelete}
        />
        <AlertDismissible
          title="Warning!"
          msg="Are you sure you want to change the status of the user account?"
          type="warning"
          showAlert={showStatusAlert}
          onConfirm={handleConfirmOnStatusChange}
        />
        <PageHeading title={"User details"} />
        <Container className={`${styles.userContainer} my-5`}>
          <Button
            variant="outline-light"
            onClick={() => navigate("/users")}
            className={`${styles.btnUserList} m-4`}
          >
            <FontAwesomeIcon icon={faList} className="list-icon-responsive" />
          </Button>{" "}
          <h2 className={`${styles.userName} shadow-sm`}>
            {userDetail?.firstName} {userDetail?.lastName}
          </h2>
          <hr />
          <Row>
            <Col md={6} xs={12} className="mt-1">
              <Card border="info">
                <Card.Header>Email</Card.Header>
                <Card.Body>
                  <Card.Subtitle className="mb-1 text-muted">
                    {userDetail?.email}
                  </Card.Subtitle>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} xs={12} className="mt-1">
              <Card border="info">
                <Card.Header>Phone</Card.Header>
                <Card.Body>
                  <Card.Subtitle className="mb-1 text-muted">
                    {userDetail?.phone}
                  </Card.Subtitle>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <Row className="my-2">
            <Col className="mt-1">
              <Card border="info" className="text-center">
                <Card.Header>Detail description</Card.Header>
                <Row className="my-3">
                  <Col xs={12}>
                    <h4>Role : {userDetail?.role}</h4>
                  </Col>
                </Row>
                <Row>
                  <Col lg={6} xs={12} md={6}>
                    <Card.Body>
                      {
                        <Card.Text>
                          Verified : {userDetail?.isEnabled ? "Yes" : "No"}
                        </Card.Text>
                      }
                    </Card.Body>
                  </Col>
                  <Col lg={6} xs={12} md={6}>
                    <Card.Body>
                      {
                        <Card.Text
                          className={
                            userDetail?.isLocked
                              ? styles.statusInActive
                              : styles.statusActive
                          }
                        >
                          Active : {userDetail?.isLocked ? "No" : "Yes"}
                        </Card.Text>
                      }
                    </Card.Body>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
          {(isAdmin || isSuperAdmin) && (
            <Row className="my-2">
              <Col className="mt-1">
                <Card border="info">
                  <Card.Header>Action</Card.Header>
                  <Row className="p-2">
                    <Col
                      lg={6}
                      xs={12}
                      className="d-flex justify-content-lg-end justify-content-center mb-2 mb-lg-0"
                    >
                      <Button
                        variant="warning"
                        className="px-5 me-2"
                        disabled={isUpdating}
                        onClick={handleStatus}
                      >
                        {userDetail.isLocked ? "Activate" : "Deactivate"}
                      </Button>
                    </Col>
                    <Col
                      lg={6}
                      xs={12}
                      className="d-flex justify-content-lg-start justify-content-center"
                    >
                      <Button
                        variant="danger"
                        className="px-5"
                        onClick={handleDelete}
                      >
                        Delete
                      </Button>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
          )}
        </Container>
      </div>
    );
  }

  return content;
};
